import type { ComponentType, SVGProps } from 'react';
import {
  BanknotesIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  CheckBadgeIcon,
  CubeIcon,
  CurrencyDollarIcon,
  EyeIcon,
  FireIcon,
  FlagIcon,
  HashtagIcon,
  RocketLaunchIcon,
  ShoppingBagIcon,
  SparklesIcon,
  StarIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import type { SellerGamificationDashboard } from '../../../types/sellerGamification.types';
import { formatMAD } from '../../../utils/funnelCopy';

const STORE_LEVEL_STYLES: Record<string, string> = {
  starter: 'bg-slate-100 text-slate-800 ring-slate-200',
  promoter: 'bg-sky-50 text-sky-900 ring-sky-200',
  explorer: 'bg-indigo-50 text-indigo-900 ring-indigo-200',
  converter: 'bg-purple-50 text-purple-900 ring-purple-200',
  scaler: 'bg-emerald-50 text-emerald-900 ring-emerald-200',
  champion: 'bg-amber-50 text-amber-950 ring-amber-200',
};

export const STORE_LEVEL_ORDER = [
  'starter',
  'promoter',
  'explorer',
  'converter',
  'scaler',
  'champion',
] as const;

export const STORE_LEVEL_LABELS: Record<string, string> = {
  starter: 'Starter',
  promoter: 'Promoter',
  explorer: 'Explorer',
  converter: 'Converter',
  scaler: 'Scaler',
  champion: 'Champion',
};

export const STORE_LEVEL_TAGLINES: Record<string, string> = {
  starter: 'Setting up your shop',
  promoter: 'Growing visibility',
  explorer: 'Testing what sells',
  converter: 'Turning visits into orders',
  scaler: 'Scaling what works',
  champion: 'Top-tier seller momentum',
};

const CATEGORY_ICONS: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  journey: RocketLaunchIcon,
  catalog: CubeIcon,
  sales: ShoppingBagIcon,
  earnings: BanknotesIcon,
  habit: FireIcon,
  quality: StarIcon,
  store: BuildingStorefrontIcon,
  profile: CheckBadgeIcon,
};

const ACHIEVEMENT_ICONS: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  STORE_LAUNCHED: RocketLaunchIcon,
  FIRST_LISTING: SparklesIcon,
  FIVE_LISTINGS: CubeIcon,
  TEN_LISTINGS: BuildingStorefrontIcon,
  FIRST_STORE_VISIT: EyeIcon,
  FIRST_SALE: ShoppingBagIcon,
  TEN_SALES: HashtagIcon,
  FIFTY_SALES: ChartBarIcon,
  HUNDRED_SALES: TrophyIcon,
  FIRST_PAYOUT: BanknotesIcon,
  EARNINGS_100: CurrencyDollarIcon,
  EARNINGS_500: CurrencyDollarIcon,
  EARNINGS_1000: CurrencyDollarIcon,
  EARNINGS_5000: TrophyIcon,
  QUALITY_LISTER: StarIcon,
  STREAK_4_WEEKS: FireIcon,
  HOT_SELLER_WEEK: ChartBarIcon,
  PROFILE_COMPLETE: CheckBadgeIcon,
  WEEKLY_CHALLENGE_COMPLETE: FlagIcon,
};

export type SellerFocus = {
  title: string;
  description: string;
  progress?: number;
  progressLabel?: string;
  remainingLabel?: string;
  ctaLabel: string;
  ctaHref: string;
  type: 'journey' | 'challenge' | 'milestone' | 'profile' | 'maintain';
  urgency: 'high' | 'normal' | 'low';
};

export function achievementIcon(
  code: string,
  category: string,
): ComponentType<SVGProps<SVGSVGElement>> {
  return ACHIEVEMENT_ICONS[code] ?? CATEGORY_ICONS[category] ?? TrophyIcon;
}

export function storeLevelIndex(level: string): number {
  const idx = STORE_LEVEL_ORDER.indexOf(level as (typeof STORE_LEVEL_ORDER)[number]);
  return idx >= 0 ? idx : 0;
}

export function storeLevelClass(level: string): string {
  return STORE_LEVEL_STYLES[level] ?? STORE_LEVEL_STYLES.starter;
}

export function formatStoreLevelLabel(level: string): string {
  return STORE_LEVEL_LABELS[level] ?? level.charAt(0).toUpperCase() + level.slice(1);
}

export function storeLevelTagline(level: string): string {
  return STORE_LEVEL_TAGLINES[level] ?? 'Building your store';
}

export function formatDeltaPercent(delta: number | null): string | null {
  if (delta == null) return null;
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta}%`;
}

export function deltaTone(delta: number | null): 'emerald' | 'rose' | 'slate' {
  if (delta == null || delta === 0) return 'slate';
  return delta > 0 ? 'emerald' : 'rose';
}

export function healthScoreTone(score: number): 'emerald' | 'amber' | 'slate' {
  if (score >= 85) return 'emerald';
  if (score >= 60) return 'amber';
  return 'slate';
}

export function formatActivityPoints(xp: number): string {
  return `${xp.toLocaleString()} activity pts`;
}

export function formatUnlockDate(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatCohortDetail(
  cohort:
    | { available: false; reason: string }
    | { available: true; topPercent: number; message: string; sellerCount: number },
): string | null {
  if (!cohort.available) return null;
  if (cohort.sellerCount < 10) return null;
  return `Benchmarked against ${cohort.sellerCount.toLocaleString()} active sellers`;
}

export const CATEGORY_LABELS: Record<string, string> = {
  journey: 'Launch',
  catalog: 'Catalog',
  sales: 'Sales',
  store: 'Store',
  earnings: 'Earnings',
  profile: 'Profile',
  habit: 'Activity',
  quality: 'Quality',
};

/** Goal-gradient effect: motivation copy when close to finishing. */
export function goalGradientCopy(pct: number, remainingLabel?: string): string | null {
  if (pct >= 100) return 'Goal complete — great work this week';
  if (pct >= 85 && remainingLabel) return `Almost there — ${remainingLabel}`;
  if (pct >= 70) return 'You are in the final stretch — finish strong';
  if (pct >= 50) return 'Halfway there — momentum is building';
  return null;
}

/** Single prioritized next action — reduces decision fatigue (implementation intention). */
export function deriveSellerFocus(data: SellerGamificationDashboard): SellerFocus {
  if (!data.journeyComplete) {
    const next = data.journey.find((s) => !s.complete);
    const completed = data.journey.filter((s) => s.complete).length;
    const total = data.journey.length;
    if (next) {
      return {
        title: next.title,
        description: next.description,
        progress: total > 0 ? Math.round((completed / total) * 100) : 0,
        progressLabel: `${completed} of ${total} launch steps done`,
        remainingLabel: `${total - completed} step${total - completed === 1 ? '' : 's'} left`,
        ctaLabel: next.ctaLabel,
        ctaHref: next.ctaHref,
        type: 'journey',
        urgency: completed === 0 ? 'high' : completed >= total - 1 ? 'high' : 'normal',
      };
    }
  }

  if (!data.profileCompletion.complete) {
    const incomplete = data.profileCompletion.checks.filter((c) => !c.complete);
    if (incomplete.length > 0) {
      return {
        title: 'Strengthen your store profile',
        description: 'Buyers trust complete profiles — finish the remaining fields to unlock your next milestone.',
        progress: data.profileCompletion.score,
        progressLabel: `${data.profileCompletion.score}% profile strength`,
        remainingLabel: `${incomplete.length} field${incomplete.length === 1 ? '' : 's'} left`,
        ctaLabel: 'Complete profile',
        ctaHref: '/seller/settings?tab=store',
        type: 'profile',
        urgency: data.profileCompletion.score >= 70 ? 'high' : 'normal',
      };
    }
  }

  const c = data.weeklyChallenge;
  if (!c.complete) {
    const pct = c.target > 0 ? Math.min(100, Math.round((c.progress / c.target) * 100)) : 0;
    const remaining = Math.max(0, c.target - c.progress);
    return {
      title: c.title,
      description: c.description,
      progress: pct,
      progressLabel: `${c.progress} / ${c.target} · ${c.weekLabel}`,
      remainingLabel:
        remaining > 0
          ? `${remaining} more to hit this week's goal`
          : undefined,
      ctaLabel: pct >= 70 ? 'Finish weekly goal' : 'See weekly goal',
      ctaHref: '/seller/progress',
      type: 'challenge',
      urgency: pct >= 70 ? 'high' : 'normal',
    };
  }

  const nextMilestone = data.lockedAchievements[0];
  if (nextMilestone) {
    return {
      title: nextMilestone.title,
      description: nextMilestone.description,
      ctaLabel: 'How to unlock',
      ctaHref: nextMilestone.href,
      type: 'milestone',
      urgency: 'normal',
    };
  }

  return {
    title: 'Keep your momentum',
    description: 'Publish, promote, and fulfill orders — progress updates automatically from your store.',
    ctaLabel: 'View full progress',
    ctaHref: '/seller/progress',
    type: 'maintain',
    urgency: 'low',
  };
}

export type SellerPageId =
  | 'dashboard'
  | 'designs'
  | 'store'
  | 'analytics'
  | 'earnings'
  | 'orders'
  | 'settings'
  | 'progress';

export function findNextMilestoneInCategories(
  data: SellerGamificationDashboard,
  categories: string[],
) {
  return data.lockedAchievements.find((a) => categories.includes(a.category));
}

/** Page-aware focus — contextual to where the seller is working. */
export function derivePageFocus(
  data: SellerGamificationDashboard,
  page: SellerPageId,
): SellerFocus {
  switch (page) {
    case 'designs': {
      const catalogMilestone = findNextMilestoneInCategories(data, ['catalog', 'quality', 'habit']);
      if (data.catalogHealth.score < 70) {
        const { catalogHealth } = data;
        const hidden =
          catalogHealth.draftOrHiddenListings > 0
            ? ` · ${catalogHealth.draftOrHiddenListings} not shop-visible`
            : '';
        return {
          title: 'Improve catalog health',
          description: `${catalogHealth.liveListings} shop-visible product${catalogHealth.liveListings === 1 ? '' : 's'} from ${catalogHealth.publishedListings} published listing${catalogHealth.publishedListings === 1 ? '' : 's'} at ${catalogHealth.score}% health${hidden}.`,
          progress: catalogHealth.score,
          progressLabel: catalogHealth.label,
          remainingLabel: `${70 - catalogHealth.score}% to reach healthy`,
          ctaLabel: 'Open design studio',
          ctaHref: '/seller/design-tool',
          type: 'milestone',
          urgency: 'normal',
        };
      }
      if (catalogMilestone) {
        return {
          title: catalogMilestone.title,
          description: catalogMilestone.description,
          ctaLabel: 'See milestone',
          ctaHref: catalogMilestone.href,
          type: 'milestone',
          urgency: 'normal',
        };
      }
      break;
    }
    case 'store': {
      const storeStep = data.journey.find((s) => s.id === 'first_store_visit' && !s.complete);
      if (storeStep) {
        return {
          title: storeStep.title,
          description: storeStep.description,
          ctaLabel: storeStep.ctaLabel,
          ctaHref: storeStep.ctaHref,
          type: 'journey',
          urgency: 'high',
        };
      }
      const storeMilestone = findNextMilestoneInCategories(data, ['journey', 'store']);
      if (storeMilestone) {
        return {
          title: storeMilestone.title,
          description: storeMilestone.description,
          ctaLabel: 'View store milestone',
          ctaHref: storeMilestone.href,
          type: 'milestone',
          urgency: 'normal',
        };
      }
      break;
    }
    case 'analytics':
    case 'orders': {
      const salesMilestone = findNextMilestoneInCategories(data, ['sales']);
      if (salesMilestone) {
        return {
          title: salesMilestone.title,
          description: salesMilestone.description,
          progress: undefined,
          ctaLabel: 'Track in analytics',
          ctaHref: salesMilestone.href,
          type: 'milestone',
          urgency: 'normal',
        };
      }
      if (!data.weeklyChallenge.complete) {
        const c = data.weeklyChallenge;
        const pct = c.target > 0 ? Math.min(100, Math.round((c.progress / c.target) * 100)) : 0;
        return {
          title: c.title,
          description: c.description,
          progress: pct,
          progressLabel: `${c.progress} / ${c.target} this week`,
          ctaLabel: 'View weekly goal',
          ctaHref: '/seller/progress',
          type: 'challenge',
          urgency: pct >= 70 ? 'high' : 'normal',
        };
      }
      break;
    }
    case 'earnings': {
      const m = data.earningsMilestones;
      if (m.nextThreshold != null) {
        return {
          title: `Reach ${formatMAD(m.nextThreshold)} lifetime gross profit`,
          description: 'Analytics gross profit from paid order lines — same source as your earnings overview.',
          progress: m.progressToNext,
          progressLabel: `${formatMAD(m.lifetimeEarnings)} earned so far`,
          remainingLabel: `${formatMAD(m.remainingToNext)} to next tier`,
          ctaLabel: 'View milestones',
          ctaHref: '/seller/progress',
          type: 'milestone',
          urgency: m.progressToNext >= 70 ? 'high' : 'normal',
        };
      }
      const earningsMilestone = findNextMilestoneInCategories(data, ['earnings']);
      if (earningsMilestone) {
        return {
          title: earningsMilestone.title,
          description: earningsMilestone.description,
          ctaLabel: 'View milestone',
          ctaHref: earningsMilestone.href,
          type: 'milestone',
          urgency: 'normal',
        };
      }
      break;
    }
    case 'settings': {
      if (!data.profileCompletion.complete) {
        return deriveSellerFocus(data);
      }
      break;
    }
    default:
      break;
  }

  return deriveSellerFocus(data);
}

export function momentumHeadline(data: SellerGamificationDashboard): string {
  if (!data.journeyComplete) {
    const completed = data.journey.filter((s) => s.complete).length;
    if (completed === 0) return 'Your launch journey starts here';
    if (completed < data.journey.length) return 'You are closer than when you started';
    return 'Launch complete — time to grow';
  }
  if (!data.weeklyChallenge.complete) {
    const pct = weeklyChallengePercent(data);
    if (pct >= 70) return 'Strong week — one push left';
    return 'This week is yours to shape';
  }
  if (data.achievements.length > 0) return 'Momentum is on your side';
  return 'Every sale starts with one step';
}

export function weeklyChallengePercent(data: SellerGamificationDashboard): number {
  const c = data.weeklyChallenge;
  if (c.target <= 0) return c.complete ? 100 : 0;
  return Math.min(100, Math.round((c.progress / c.target) * 100));
}

/** Hide dashboard weekly card when global strip already shows strong weekly progress. */
export function shouldShowDashboardWeeklyCard(data: SellerGamificationDashboard | null | undefined): boolean {
  if (!data) return true;
  if (data.weeklyChallenge.complete) return false;
  return weeklyChallengePercent(data) < 70;
}

export function formatPublishGamificationMessage(
  before: SellerGamificationDashboard | null,
  after: SellerGamificationDashboard,
): string {
  const xpDelta = before ? after.xp - before.xp : 0;
  const newUnlocks = before
    ? after.pendingCelebrations.filter(
        (c) => !before.pendingCelebrations.some((p) => p.code === c.code),
      )
    : after.pendingCelebrations;

  if (newUnlocks.length > 0) {
    const top = newUnlocks[0];
    return `Listing published! +${top.xp} pts — ${top.title} unlocked`;
  }
  if (xpDelta > 0) {
    return `Listing published! +${xpDelta} activity pts earned`;
  }
  const week = after.weeklyChallenge;
  if (!week.complete && week.progress > (before?.weeklyChallenge.progress ?? 0)) {
    return `Listing published! Progress toward this week's goal (${week.progress}/${week.target})`;
  }
  return 'Listing published successfully!';
}
